<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-agency-dialog"
    z-index="3000"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-4 px-sm-6 py-3 py-sm-4"
      >
        <div class="text-subtitle-1 text-sm-h5 font-weight-bold white--text d-flex align-center">
          <v-icon large color="white" class="mr-2 mr-sm-3">mdi-trophy</v-icon>
          <span>Create Team Challenges</span>
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          small
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-4 px-sm-6 pt-4 pb-1 text-caption text-sm-body-2 text--secondary">
        <div class="d-flex align-center flex-wrap">
          <v-icon color="primary" class="mr-2" x-small>mdi-information-outline</v-icon>
          <span>Create challenges and invite agencies to participate</span>
        </div>
      </v-card-text>

      <!-- Messages -->
      <v-expand-transition>
        <v-card-text class="px-4 px-sm-6 py-0" v-if="message || errorMessage">
          <v-alert
            :type="messageType"
            class="mb-3 elevation-2"
            transition="scale-transition"
            :icon="messageType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"
            dense
            dismissible
            @input="clearMessages"
          >
            {{ message || errorMessage }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Main Content -->
      <v-card-text class="px-4 px-sm-6 py-2" style="max-height: 55vh; overflow-y: auto">
        <v-row dense>
          <!-- Title -->
          <v-col cols="12" class="py-1">
            <v-text-field
              v-model="competition.title"
              label="Challenge Title*"
              outlined
              dense
              prepend-inner-icon="mdi-format-title"
              :rules="[rules.required]"
              class="modern-input"
              hide-details="auto"
              background-color="white"
            />
          </v-col>

          <!-- Description -->
          <v-col cols="12" class="py-1">
            <v-textarea
              v-model="competition.description"
              label="Description*"
              outlined
              dense
              rows="3"
              auto-grow
              prepend-inner-icon="mdi-text"
              :rules="[rules.required]"
              class="modern-input"
              hide-details="auto"
              background-color="white"
            />
          </v-col>

          <!-- Start Date -->
          <v-col cols="12" sm="6" class="py-1">
            <v-menu
              v-model="start_date_picker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDateForDisplay(competition.start_date)"
                  label="Start Date*"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="[rules.required]"
                  class="modern-input"
                  hide-details="auto"
                  background-color="white"
                />
              </template>
              <v-date-picker
                v-model="start_date"
                @input="saveStartDate"
                :min="today"
              />
            </v-menu>
          </v-col>

          <!-- End Date -->
          <v-col cols="12" sm="6" class="py-1">
            <v-menu
              v-model="end_date_picker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDateForDisplay(competition.end_date)"
                  label="End Date*"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="[rules.required]"
                  class="modern-input"
                  hide-details="auto"
                  background-color="white"
                />
              </template>
              <v-date-picker
                v-model="end_date"
                @input="saveEndDate"
                :min="start_date || today"
              />
            </v-menu>
          </v-col>

          <!-- Target Points -->
          <v-col cols="12" class="py-1">
            <v-text-field
              v-model="competition.target_points"
              label="Target Points*"
              outlined
              dense
              type="number"
              min="1"
              prepend-inner-icon="mdi-numeric"
              :rules="[rules.required, rules.minValue]"
              @input="onTarget"
              class="modern-input"
              hide-details="auto"
              background-color="white"
            />
          </v-col>

          <!-- Agency Selection with Search -->
          <v-col cols="12" class="py-1">
            <div class="d-flex align-center mb-1">
              <span class="font-weight-medium text-body-2">Search and Select Agencies*</span>
              <v-tooltip bottom max-width="280">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="ml-1"
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    :ripple="false"
                  >
                    <v-icon x-small color="primary">mdi-information</v-icon>
                  </v-btn>
                </template>
                <span>Followed Agencies Will be allowed to invite</span>
              </v-tooltip>
            </div>
            
            <v-autocomplete
              v-model="selectedAgencies"
              :items="filteredAgencies"
              item-text="displayName"
              item-value="_id"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              prepend-inner-icon="mdi-account-multiple"
              :rules="[rules.required]"
              placeholder="Search agencies by name or email..."
              :search-input.sync="agencySearch"
              class="modern-input agency-autocomplete"
              :loading="loading"
              return-object
              hide-no-data
              no-filter
              max-height="250"
            >
              <!-- Custom Search Input -->
              <template v-slot:prepend-inner>
                <v-icon small color="primary" class="mr-2">mdi-magnify</v-icon>
              </template>

              <!-- Prepend: Select All -->
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  @click="toggleSelectAll"
                  v-if="filteredAgencies.length > 0"
                  class="select-all-item"
                >
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="allSelected"
                      color="primary"
                      dense
                      hide-details
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium text-body-2">
                      <v-icon small class="mr-2">mdi-select-all</v-icon>
                      Select All ({{ filteredAgencies.length }})
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider v-if="filteredAgencies.length > 0" class="my-1" />
              </template>

              <!-- Item Template -->
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on" class="agency-list-item">
                  <v-list-item-avatar size="32" class="mr-3">
                    <v-img
                      :src="item.midThumbnail || getDefaultAvatar(item.name)"
                      :alt="item.name"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium text-body-2">
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-email</v-icon>
                      {{ item.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="isItemSelected(item)"
                      color="primary"
                      dense
                      hide-details
                      @click.stop
                    />
                  </v-list-item-action>
                </v-list-item>
              </template>

              <!-- Selection Chips -->
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 3"
                  :key="item._id"
                  label
                  small
                  class="agency-chip mr-1 mb-1"
                  close
                  @click:close="removeAgency(item)"
                >
                  <v-avatar left size="18" class="mr-1">
                    <v-img
                      :src="item.midThumbnail || getDefaultAvatar(item.name)"
                      :alt="item.name"
                    />
                  </v-avatar>
                  <span class="font-weight-medium text-caption">{{ item.name }}</span>
                </v-chip>
                <v-chip
                  v-if="index === 3"
                  :key="'more'"
                  label
                  small
                  class="agency-chip mr-1 mb-1"
                  color="primary"
                  text-color="white"
                >
                  +{{ selectedAgencies.length - 3 }} more
                </v-chip>
              </template>

              <!-- No Data / Add New -->
              <template v-slot:no-data>
                <v-list-item @click="addNewAgency" class="add-new-item">
                  <v-list-item-avatar size="32" class="mr-3" color="primary lighten-4">
                    <v-icon color="primary" small>mdi-plus</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium primary--text text-body-2">
                      Add "{{ agencySearch || 'New Agency' }}"
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      <v-icon x-small class="mr-1">mdi-plus-circle</v-icon>
                      Create new agency
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>

            <!-- Selected count badge -->
            <div class="d-flex justify-end mt-1" v-if="selectedAgencies.length > 0">
              <span class="selected-count text-caption">
                {{ selectedAgencies.length }} agency(ies) selected
              </span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-4 px-sm-6 pb-4 pb-sm-6 pt-2 d-flex flex-wrap gap-2">
        <v-btn
          text
          @click="onClose"
          color="grey darken-1"
          class="px-4 flex-grow-1 flex-sm-grow-0"
          large
        >
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          :loading="saving"
          class="px-6 save-btn flex-grow-1 flex-sm-grow-0"
          large
          dark
        >
          <template v-slot:loader>
            <v-icon light>mdi-loading mdi-spin</v-icon>
          </template>
          <v-icon left>mdi-plus</v-icon>
          Create Challenge
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import DOMPurify from "dompurify";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      // UI State
      message: null,
      messageType: "error",
      saving: false,
      loading: false,
      errorMessage: null,
      agencySearch: "",
      
      // Form
      competition: {
        agency_id: "",
        title: "",
        description: "",
        start_date: "",
        end_date: "",
        requested_agency_ids: [],
        target_points: ""
      },
      
      // Agency data
      agencyList: [],
      selectedAgencies: [],
      allSelected: false,
      
      // Date pickers
      start_date_picker: false,
      end_date_picker: false,
      start_date: null,
      end_date: null,
      
      // Rules
      rules: {
        required: value => {
          if (Array.isArray(value)) {
            return value.length > 0 || "Please select at least one agency";
          }
          return !!value || "This field is required";
        },
        minValue: value => {
          if (value && Number(value) < 1) {
            return "Target points must be at least 1";
          }
          return true;
        }
      }
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    
    // Filter agencies based on search
    filteredAgencies() {
      if (!this.agencySearch || this.agencySearch.trim() === "") {
        return this.agencyList;
      }
      const search = this.agencySearch.toLowerCase().trim();
      return this.agencyList.filter(agency => {
        const name = (agency.name || "").toLowerCase();
        const email = (agency.email || "").toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    },
    
    today() {
      const date = new Date();
      return date.toISOString().substr(0, 10);
    },
    
    tomorrow() {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      return date.toISOString().substr(0, 10);
    }
  },
  methods: {
    ...mapActions({
      addCompetition: "competition/addCompetition",
      getCompetition: "competition/getCompetition",
      fetchEachCompetitions: "competition/fetchEachCompetitions"
    }),
    
    // Toggle select all agencies
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedAgencies = [];
      } else {
        this.selectedAgencies = [...this.filteredAgencies];
      }
      this.allSelected = !this.allSelected;
      this.updateCompetitionAgencies();
    },
    
    // Check if item is selected
    isItemSelected(item) {
      return this.selectedAgencies.some(agency => agency._id === item._id);
    },
    
    // Remove agency from selection
    removeAgency(item) {
      const index = this.selectedAgencies.findIndex(a => a._id === item._id);
      if (index > -1) {
        this.selectedAgencies.splice(index, 1);
        this.updateCompetitionAgencies();
        this.updateAllSelectedState();
      }
    },
    
    // Update competition with selected agencies
    updateCompetitionAgencies() {
      this.competition.requested_agency_ids = this.selectedAgencies.map(a => a._id);
    },
    
    // Update all selected state
    updateAllSelectedState() {
      const allIds = this.filteredAgencies.map(a => a._id);
      const selectedIds = this.selectedAgencies.map(a => a._id);
      this.allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
    },
    
    // Get default avatar
    getDefaultAvatar(name) {
      if (!name) return 'https://via.placeholder.com/40/084468/fff?text=?';
      const initials = name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
      return `https://via.placeholder.com/40/084468/fff?text=${initials}`;
    },
    
    // Add new agency
    async addNewAgency() {
      const name = this.agencySearch?.trim();
      if (!name || name.length < 2) {
        this.showMessage("Please enter at least 2 characters for agency name", "error");
        return;
      }

      // Check if exists
      const exists = this.agencyList.some(
        item => item.name.toLowerCase() === name.toLowerCase()
      );
      if (exists) {
        this.showMessage("Agency already exists in the list", "error");
        return;
      }

      this.loading = true;
      try {
        const newAgency = {
          _id: `temp_${Date.now()}`,
          name: this.sanitizeInput(name),
          email: `${this.sanitizeInput(name).toLowerCase().replace(/\s+/g, '.')}@agency.com`,
          midThumbnail: this.getDefaultAvatar(name)
        };

        // Add to list and select
        this.agencyList.unshift(newAgency);
        this.selectedAgencies.push(newAgency);
        this.updateCompetitionAgencies();
        this.updateAllSelectedState();
        this.agencySearch = "";
        
        this.showMessage(`Agency "${newAgency.name}" added successfully!`, "success");
      } catch (error) {
        this.showMessage(error?.message || "Failed to add agency", "error");
      } finally {
        this.loading = false;
      }
    },
    
    // Show message
    showMessage(text, type = "error") {
      this.message = text;
      this.messageType = type;
      // Don't auto-clear errors so user can see them
      if (type === "success") {
        setTimeout(() => {
          this.clearMessages();
        }, 3000);
      }
    },
    
    clearMessages() {
      this.message = null;
      this.errorMessage = null;
    },
    
    // Sanitize input
    sanitizeInput(value) {
      if (!value) return value;
      let clean = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });
      if (!clean.trim()) {
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }
      return clean.trim();
    },
    
    // Handle target points input
    onTarget(value) {
      const numericValue = String(value).replace(/[^0-9]/g, "");
      if (numericValue === "" || Number(numericValue) < 1) {
        this.competition.target_points = 1;
      } else {
        this.competition.target_points = Number(numericValue);
      }
    },
    
    // Validate dates
    validateDates() {
      if (!this.competition.start_date || !this.competition.end_date) {
        return { valid: false, message: "Please select both start and end dates" };
      }

      const start = new Date(this.competition.start_date);
      const end = new Date(this.competition.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check if start date is in the past
      if (start < today) {
        return { valid: false, message: "Start date cannot be in the past. Please select a future date." };
      }

      // Check if end date is before start date
      if (end <= start) {
        return { valid: false, message: "End date must be after start date" };
      }

      return { valid: true };
    },
    
    // Save challenge
    async onSave() {
      this.clearMessages();

      // Validate title
      const sanitizedTitle = this.sanitizeInput(this.competition.title);
      if (!sanitizedTitle) {
        this.showMessage("Please enter a title", "error");
        return;
      }
      if (/[^a-zA-Z\s]/.test(sanitizedTitle)) {
        this.showMessage("Title can only contain letters and spaces", "error");
        return;
      }

      // Validate description
      const sanitizedDescription = this.sanitizeInput(this.competition.description);
      if (!sanitizedDescription) {
        this.showMessage("Please enter a description", "error");
        return;
      }

      // Validate dates
      const dateValidation = this.validateDates();
      if (!dateValidation.valid) {
        this.showMessage(dateValidation.message, "error");
        return;
      }

      // Validate target points
      if (!this.competition.target_points || this.competition.target_points < 1) {
        this.showMessage("Please enter valid target points (minimum 1)", "error");
        return;
      }

      // Validate agencies
      if (!this.selectedAgencies.length) {
        this.showMessage("Please select at least one agency", "error");
        return;
      }

      // Prepare params
      this.competition.agency_id = this.profile._id;
      const params = {
        ...this.competition,
        title: sanitizedTitle,
        description: sanitizedDescription,
        join_type: "Invitation",
        requested_agency_ids: this.selectedAgencies.map(a => a._id)
      };

      this.saving = true;
      try {
        await this.addCompetition(params);
        this.showMessage("Team challenge created successfully!", "success");
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        console.error("Error creating challenge:", error);
        
        // Extract error message from the response
        let errorMsg = "An error occurred while creating the challenge";
        
        // Check if error has response data with message
        if (error && error.response && error.response.data) {
          const responseData = error.response.data;
          console.log("Response data:", responseData);
          
          // The API returns { status: false, message: "Error message" }
          if (responseData.message) {
            errorMsg = responseData.message;
          } else if (responseData.error) {
            errorMsg = responseData.error;
          } else if (typeof responseData === 'string') {
            errorMsg = responseData;
          }
        } else if (error && error.message) {
          // If error is a string directly
          if (typeof error === 'string') {
            errorMsg = error;
          } else if (error.message) {
            errorMsg = error.message;
          }
        } else if (typeof error === 'string') {
          errorMsg = error;
        }
        
        // Make error messages user-friendly
        if (errorMsg.toLowerCase().includes("start date must be greater than current date")) {
          errorMsg = "Start date must be in the future. Please select a date after today.";
        } else if (errorMsg.toLowerCase().includes("start date must be before end date")) {
          errorMsg = "Start date must be before end date. Please adjust your dates.";
        } else if (errorMsg.toLowerCase().includes("already exists")) {
          errorMsg = "A challenge with this title already exists. Please use a different title.";
        }
        
        this.showMessage(errorMsg, "error");
        this.saving = false;
        return;
      } finally {
        this.saving = false;
      }
    },
    
    // Reset form
    reset() {
      this.competition = {
        agency_id: "",
        title: "",
        description: "",
        start_date: "",
        end_date: "",
        requested_agency_ids: [],
        target_points: ""
      };
      this.selectedAgencies = [];
      this.allSelected = false;
      this.agencySearch = "";
      this.clearMessages();
      this.start_date = null;
      this.end_date = null;
    },
    
    // Date functions
    saveStartDate(date) {
      this.competition.start_date = date;
      this.start_date_picker = false;
    },
    
    saveEndDate(date) {
      this.competition.end_date = date;
      this.end_date_picker = false;
    },
    
    formatDateForDisplay(dateStr) {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-");
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric"
      });
    },
    
    // Load data
    loadagencyList() {
      const agencyId = this.profile._id;
      this.loading = true;
      this.getCompetition({ agencyId })
        .then(res => {
          this.agencyList = res || [];
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.showMessage(error.response?.data?.message || "Failed to load agencies", "error");
        });
    },
    
    loadCompetitions() {
      // Optional: load competitions if needed
    }
  },
  
  mounted() {
    this.loadagencyList();
  },
  
  watch: {
    dialog(val) {
      if (!val) {
        this.reset();
      }
    },
    
    // Update all selected state when filtered list changes
    filteredAgencies() {
      this.updateAllSelectedState();
    },
    
    // Sync selected agencies with competition
    selectedAgencies: {
      handler() {
        this.updateCompetitionAgencies();
        this.updateAllSelectedState();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
/* Dialog */
.modern-agency-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  width: 100%;
  max-width: 600px;
  margin: 12px;
}

@media (max-width: 400px) {
  .modern-agency-dialog {
    margin: 8px;
    border-radius: 12px !important;
    max-width: calc(100vw - 16px);
  }
}

/* Header */
.editor-header {
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

/* Inputs */
.modern-input {
  width: 100%;
}

.modern-input ::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s ease;
  background-color: white !important;
}

.modern-input ::v-deep .v-messages {
  min-height: 0;
}

/* Agency Autocomplete */
.agency-autocomplete {
  width: 100%;
}

.agency-autocomplete ::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s ease;
  background-color: white !important;
  min-height: 56px !important;
  flex-wrap: wrap;
}

.agency-autocomplete ::v-deep .v-select__selections {
  padding: 4px 0;
  flex-wrap: wrap;
  gap: 4px;
}

.agency-autocomplete ::v-deep .v-select__selection {
  max-width: 100%;
}

/* Chips */
.agency-chip {
  background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%) !important;
  border: 1px solid rgba(58, 123, 213, 0.2) !important;
  border-radius: 20px !important;
  transition: all 0.2s ease;
  max-width: 100%;
}

.agency-chip:hover {
  background: linear-gradient(135deg, #e8eeff 0%, #dce6ff 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(58, 123, 213, 0.15);
}

.agency-chip .v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

/* List items */
.agency-list-item {
  transition: all 0.2s ease;
  min-height: 48px;
}

.agency-list-item:hover {
  background: rgba(58, 123, 213, 0.05) !important;
}

.select-all-item {
  min-height: 44px;
}

.add-new-item {
  min-height: 48px;
  border-top: 1px solid #e0e0e0;
}

.add-new-item:hover {
  background: rgba(58, 123, 213, 0.05) !important;
}

.v-list-item-avatar {
  border: 2px solid rgba(58, 123, 213, 0.1);
  border-radius: 50% !important;
  overflow: hidden;
  flex-shrink: 0;
}

/* Selected count */
.selected-count {
  background: rgba(58, 123, 213, 0.08);
  color: #084468;
  padding: 2px 12px;
  border-radius: 12px;
  font-weight: 500;
}

/* Buttons */
.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
  border-radius: 20px !important;
  min-width: 140px;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

/* Card actions responsive */
.v-card-actions {
  gap: 8px;
}

@media (max-width: 599px) {
  .v-card-actions {
    flex-direction: column-reverse;
    padding: 12px 16px !important;
  }
  
  .v-card-actions .v-btn {
    width: 100% !important;
    margin: 4px 0 !important;
    justify-content: center;
  }
  
  .v-card-actions .v-btn:first-child {
    margin-top: 8px !important;
  }
}

@media (min-width: 600px) {
  .v-card-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

/* Menu dropdown */
.v-menu__content {
  max-height: 250px !important;
  width: auto !important;
  min-width: 200px !important;
}

@media (max-width: 400px) {
  .v-menu__content {
    max-width: calc(100vw - 32px) !important;
  }
}

/* Scrollbar */
.v-menu__content::-webkit-scrollbar,
.modern-agency-dialog::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.v-menu__content::-webkit-scrollbar-track,
.modern-agency-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.v-menu__content::-webkit-scrollbar-thumb,
.modern-agency-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #084468 0%, #1e658d 100%);
  border-radius: 10px;
}

/* Responsive text */
@media (max-width: 400px) {
  .text-subtitle-1 {
    font-size: 0.9rem !important;
  }
  .text-h5 {
    font-size: 1rem !important;
  }
}

/* Utilities */
.flex-grow-1 {
  flex-grow: 1;
}

.flex-sm-grow-0 {
  flex-grow: 0;
}

.gap-2 {
  gap: 8px;
}

@media (min-width: 600px) {
  .flex-sm-grow-0 {
    flex-grow: 0 !important;
  }
}

/* Tooltip custom styles */
.v-tooltip__content {
  background: #084468 !important;
  color: white !important;
  border-radius: 8px !important;
  padding: 8px 12px !important;
  font-size: 0.8rem !important;
  line-height: 1.4 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  max-width: 280px !important;
}

/* Responsive grid */
@media (max-width: 600px) {
  .v-col-sm-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

/* Card content responsive */
@media (max-width: 400px) {
  .v-card-text {
    padding: 8px 12px !important;
  }
  
  .v-card-title {
    padding: 12px 12px !important;
  }
  
  .v-card-actions {
    padding: 12px !important;
  }
}

/* Fix for mobile date picker */
@media (max-width: 400px) {
  .v-date-picker {
    width: 100% !important;
    max-width: calc(100vw - 32px) !important;
  }
  
  .v-date-picker .v-picker__body {
    padding: 0 !important;
  }
}
</style>